import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Claim, ClaimType } from '../../models/Claim';
import { Observable, of } from 'rxjs';
import { take, switchMap, catchError } from 'rxjs/operators';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { ClaimsService } from '../claim/claims.service';
import { Business } from '../../models/Business';
import { BusinessUser } from '../../models/BusinessUser';
import { BusinessInvite } from '../../models/BusinessInvite';
import { AuthService } from '../auth/auth.service';
import { ApiUrlService } from '../api/api-url.service';

export interface InviteUserRequest {
  businessId: number;
  email: string;
}

export interface RespondToInviteRequest {
  businessId: number;
  token: string;
  inviteStatus: string;
}

export interface PromoteUserRequest {
  businessId: number;
  userId: number;
  newRole: string;
}

@Injectable({
  providedIn: 'root',
})
export class BusinessManagementService {
  private readonly authService: AuthService = inject(AuthService);
  private readonly apiUrlService = inject(ApiUrlService);
  private get businessManagementApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/business-management`); }

  private readonly _claims = signal<Claim[]>([]);
  readonly claims: Signal<Claim[]> = this._claims.asReadonly();
  readonly claims$ = toObservable(this._claims);

  private readonly _currentBusinessUser = signal<BusinessUser | null>(null);
  readonly currentBusinessUser: Signal<BusinessUser | null> = this._currentBusinessUser.asReadonly();
  readonly businessUser$ = toObservable(this._currentBusinessUser);

  constructor(
    private httpClient: HttpClientWrapperService,
    private claimsService: ClaimsService
  ) {}

  submitBusinessClaim(claim: Claim, files: File[], profileImage?: File | null): Observable<Claim> {
    claim.claimType = ClaimType.BUSINESS;
    return this.claimsService.submitClaim(claim, files, profileImage);
  }

  loadBusinessClaimsByUserId(userId: string): void {
    this.getBusinessClaimsByUserId(userId).subscribe({
      next: (claims: Claim[]) => this._claims.set(claims)
    });
  }

  getBusinessClaimsByUserId(userId: string): Observable<Claim[]> {
    return this.claimsService.getClaimsByUserId(userId, ClaimType.BUSINESS);
  }

  getBusinessClaim(id: string): Observable<Claim> {
    return this.claimsService.getClaim(id);
  }

  getPermittedBusinesses(): Observable<Business[]> {
    return this.httpClient.get<Business[]>(this.apiUrlService.buildUrl(`/api/v1/permitted-businesses`));
  }

  inviteUser(request: InviteUserRequest): Observable<{ message: string }> {
    return this.httpClient.post<{ message: string }, InviteUserRequest>(`${this.businessManagementApiUrl}/invite-user`, request);
  }

  respondToInvite(request: RespondToInviteRequest): Observable<BusinessUser> {
    return this.httpClient.postWithoutAuth<BusinessUser, RespondToInviteRequest>(`${this.businessManagementApiUrl}/respond-to-invite`, request);
  }

  getBusinessUsers(businessId: string): Observable<BusinessUser[]> {
    return this.httpClient.get<BusinessUser[]>(`${this.businessManagementApiUrl}/${businessId}/users`);
  }

  getBusinessInvitations(businessId: string): Observable<BusinessInvite[]> {
    return this.httpClient.get<BusinessInvite[]>(`${this.businessManagementApiUrl}/${businessId}/invitations`);
  }

  removeUserAccess(businessId: string, userId: string): Observable<{ message: string }> {
    return this.httpClient.delete<{ message: string }>(`${this.businessManagementApiUrl}/remove-access?businessId=${businessId}&userId=${userId}`);
  }

  cancelInvitation(businessId: string, invitationId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.businessManagementApiUrl}/cancel-invitation?businessId=${businessId}&invitationId=${invitationId}`);
  }

  resendInvitation(businessId: string, email: string): Observable<void> {
    return this.httpClient.post<void, null>(`${this.businessManagementApiUrl}/resend-invitation?businessId=${businessId}&email=${encodeURIComponent(email)}`, null);
  }

  promoteUser(request: PromoteUserRequest): Observable<void> {
    return this.httpClient.post<void, PromoteUserRequest>(`${this.businessManagementApiUrl}/promote-user`, request);
  }

  getCurrentBusinessUser(businessId: string): Observable<BusinessUser> {
    return this.httpClient.get<BusinessUser>(`${this.businessManagementApiUrl}/${businessId}/users/me`);
  }

  setCurrentBusinessUser(businessUser: BusinessUser | null): void {
    this._currentBusinessUser.set(businessUser);
  }

  loadCurrentBusinessUser(businessId: string): void {
    this.authService.user$.pipe(
      take(1),
      switchMap((currentUser) => {
        if (!currentUser?.id) {
          return of(null);
        }
        return this.getCurrentBusinessUser(businessId);
      }),
      catchError(() => of(null))
    ).subscribe({
      next: (businessUser) => {
        this._currentBusinessUser.set(businessUser);
      }
    });
  }

  clearCurrentBusinessUser(): void {
    this._currentBusinessUser.set(null);
  }
}
